// Interfaces —— 所有 app 層程式碼皆依賴介面,不依賴具體 adapter
export type { RulesEngine, ValidationResult } from './rulesEngine';
export type { AuthProvider, LoginResult } from './authProvider';
export type { PrefsStoreService, Preferences } from './prefsStore';
export type { RealtimeBus, RealtimeEvent } from './realtimeBus';
export type { AudioUploadsApi, UploadedAudio, UploadsService,
  ImageUploadsApi,
  VideoUploadsApi,
  CreateImageIntentRequest,
  CreateVideoIntentRequest, } from './uploadsService';
export type {
  BrandKitsService,
  BrandKitCreateRequest,
  BrandKitUpdateRequest,
  CreateLogoIntentRequest,
} from './brandKitsService';
export type {
  ProjectsService,
  ProjectCreateRequest,
  ProjectUpdateRequest,
} from './projectsService';
export type {
  GroupsService,
  GroupUpdateRequest,
  AddMemberRequest,
} from './groupsService';
export type {
  MeService,
  MeResponse,
  MePatchRequest,
  AvatarIntentRequest,
  AvatarIntentResult,
  ProfileLocale,
} from './meService';
export type { CatalogService } from './catalogService';
export type {
  ComplianceService,
  ComplianceScanResult,
  ComplianceAdvisory,
  ComplianceGate,
} from './complianceService';
export type { HelpService, HelpListArticlesOptions } from './helpService';
export type { DashboardService } from './dashboardService';
export type { CanvasAiService } from './canvasAiService';
export type { AssistService } from './assistService';
export type { ContractService } from './contractService';
export type { DagService, DagNodesService, DagEdgesService } from './dagService';
export type {
  JobsService,
  JobSubmitRequest,
  JobContinueOptions,
  IdempotencyOptions,
} from './jobsService';
export type { AssetsService, ListAssetsOptions } from './assetsService';
export type { AssetLibrary, GcsPutFn } from './assetLibrary';
export type { AssetLibrariesService } from './assetLibrariesService';
export type { AdsService } from './adsService';
export type { CheckService, CheckStartTarget } from './checkService';
export { CHECK_SERVICE_SUPPORTED, isCheckDemoEnabled } from './checkService';
export type { SupportService, SupportSubmitResult } from './supportService';
export type { RedeemCodesService } from './redeemCodesService';
export type { ReferralService } from './referralService';
export type { EditsService, EditDoc, RenderEditResult } from './editsService';
// R363 — 剪輯配樂上傳的 GCS PUT(cut feature 自組 RenderDeps 用;container 內只在 assetLibrary 注入)
export { createHttpGcsPut } from './http';
export { SUPPORT_REPORT_SUPPORTED } from './supportService';
export {
  createAssetLibrary,
  ALLOWED_IMAGE_MIME_TYPES,
  ALLOWED_VIDEO_MIME_TYPES,
  IMAGE_MAX_SIZE_BYTES,
  LOGO_MAX_SIZE_BYTES,
  VIDEO_CARD_IMAGE_MAX_SIZE_BYTES,
  VIDEO_MAX_SIZE_BYTES,
  VIDEO_UPLOAD_MAX_SECONDS_DISPLAY,
  noteVideoUploadMaxSeconds,
  videoUploadMaxSecondsHint,
  validateImageFile,
  validateVideoFile,
} from './assetLibrary';

import { config } from '@/config';
import { getCurrentGroupId } from '@/stores/groupContextStore';
import { clearAllSessionStores } from '@/stores/sessionReset';

import { createAssetLibrary } from './assetLibrary';
import { isCheckDemoEnabled } from './checkService';
// R413 — 只經 `session.ts` 碰 Firebase:這支檔案在主 bundle 上,靜態 import `./firebase/auth`
// 或 `./firebase/init` 會把整包 SDK(≈ 640 KB)拉回每一頁(含官網 / 登入頁)。
import { detachAll as detachAllFirestoreListeners } from './firebase/listenerRegistry';
import {
  resetLoadedFirebaseSignIn,
  setFirebaseTokenFetcher,
  signOutLoadedFirebase,
  type FirebaseTokenResponse,
} from './firebase/session';
import {
  createHttpAdsService,
  createHttpAssetLibrariesService,
  createHttpAssetsService,
  createHttpAssistService,
  createHttpAuthProvider,
  createHttpBrandKitsService,
  createHttpCanvasAiService,
  createHttpCatalogService,
  createHttpCheckService,
  createHttpSupportService,
  createHttpComplianceService,
  createHttpContractService,
  createHttpGcsPut,
  createHttpDagService,
  createHttpDashboardService,
  createHttpGroupsService,
  createHttpHelpService,
  createHttpJobsService,
  createHttpMeService,
  createHttpProjectsService,
  createHttpRedeemCodesService,
  createHttpReferralService,
  createHttpEditsService,
  createHttpUploadsService,
  type HttpClientConfig,
} from './http';
import { httpRequestWithRefresh } from './http/httpApiClient';
import { createFixtureCheckService } from './fixtureCheckService';
import { createPrefsStore } from './prefsStore';
import { createRealtimeBus } from './realtimeBus';
import { createRulesEngine } from './rulesEngine';

/**
 * Backend wiring info(給 boot banner / debug log 用)。
 *
 * R41.6 起砍 mock layer:沒有 `SERVICE_MODE='mock'` 路徑了,FE 永遠對真 BE,
 * graph 永遠走 BFF(Cloud Run + Firestore)。`baseUrl` 空字串代表走相對路徑 +
 * vite proxy(dev)/ nginx envsubst(prod)。
 *
 * 機制 A:base url 改讀單一讀取點 `config.apiBaseUrl`。
 *
 * **proxy target 不在 client bundle**:`VITE_API_PROXY_TARGET` 是 dev server(Node)
 * 端的概念,由 `vite.config.ts` 透過 `loadEnv` 讀來設定 `/api` proxy,不屬於 runtime
 * config 契約(無 `proxyTarget` 欄位),client 在 prod 也看不到它。因此這裡只以
 * client 真正可見的 `apiBaseUrl` 當 wiring 訊號(空 = same-origin via proxy / nginx)。
 */
export const SERVICE_API_BASE_URL: string = config.apiBaseUrl;

// Boot banner — production 跟 dev 都印,讓部署到 Cloud Run 後開 F12 一眼看到當下
// backend wiring(R41.0 拿掉 `import.meta.env.DEV` gate;R41.6 拿掉 mode 顯示因為
// 只有 real;機制 A 拿掉 proxyTarget — 見上方 SERVICE_API_BASE_URL JSDoc)。
console.info(
  '%c[boot] backend api=%s env=%s',
  // eslint-disable-next-line hai/no-hardcoded-colors -- console %c 樣式字串,無 DOM 上下文、CSS var 不解析
  'color:#5eead4',
  SERVICE_API_BASE_URL || '(relative + vite proxy / nginx)',
  config.appEnv,
);

// 死寂模式 production observability — vite fail-fast 守 dev server(那層才看得到
// VITE_API_PROXY_TARGET)。production build 跑在 Cloud Run nginx 後面 same-origin,
// 前端不感知 BE upstream,vite 那層擋不到。仍可能踩到:nginx envsubst 沒帶 API_BASE_URL
// 且 upstream 沒接好。
//
// dev 故意走空 baseUrl(same-origin + vite proxy)是正常狀態,不該 warn;只在非 dev
// (prod build / runtime config 標 prod)且 baseUrl 仍空時 red-warn 一次,開 F12 即見。
if (config.appEnv !== 'dev' && !SERVICE_API_BASE_URL) {
  console.error(
    '%c[boot] ⚠️ prod build but apiBaseUrl is empty. fetch will hit same-origin only — verify nginx upstream is wired.',
    // eslint-disable-next-line hai/no-hardcoded-colors -- console %c 樣式字串,無 DOM 上下文、CSS var 不解析
    'color:#ef4444;font-weight:bold',
  );
}

const httpConfig: HttpClientConfig = {
  baseUrl: SERVICE_API_BASE_URL,
  // 認證走 HttpOnly cookie;不再從 store 取 access token 加 Bearer header。
  // **R45 起無 refresh chain**(spec §1.4 — `/api/front_auth/refresh` 410 Gone),401
  // + AUTH_INVALID_TOKEN 直接通知 app 層清 store;RequireAuth 觀察 user === null 後
  // 自動 `<Navigate to="/login">`,不在這裡硬塞 router 邏輯。
  //
  // 7 個 user-scoped store 的清除收斂在 `clearAllSessionStores`(見其 JSDoc 的「為什麼集中」),
  // 與手動登出(ThemeMenu)共用單一出口,避免兩條 auth-exit 路徑分歧導致跨 user 資料殘留。
  onAuthLost: () => {
    // R37 / R41.2 / R45 — 順序固定(對齊 ADR-002 onAuthLost):
    //   1. detach all Firestore listener(避免用過期 token 噴 permission-denied toast)
    //   2. firebaseAuth.signOut() + reset ensureFirebaseSignIn promise cache
    //      (避免下次 login 時舊 promise 還 resolved 拿到過期 user)
    //   3. clear stores(R45 加 groupContextStore — 別 user 的 group context 不可漏進來)
    //   4. RequireAuth 觀察 store 變化自然 navigate('/login')
    // R413 — SDK 沒載入過(這個分頁沒發起過即時訂閱)= 沒有 Firebase session,後兩步自動略過。
    detachAllFirestoreListeners();
    signOutLoadedFirebase().catch((err: unknown) => {
      console.error('[auth] firebaseAuth signOut failed', err);
    });
    resetLoadedFirebaseSignIn();
    // 7 個 user-scoped store 清空收斂進 clearAllSessionStores(與手動登出共用單一出口,
    // 避免兩條 auth-exit 路徑分歧導致跨 user 資料殘留)。
    clearAllSessionStores();
  },
  // R45 起每個非 exempt path 自動帶 X-Current-Group(spec §2.3)。getter 從
  // localStorage-backed zustand store 取;auth bootstrap 流程尚未拿到 gid 時回 null,
  // httpRequest 自動 skip 注入(BE 自然回 400 CURRENT_GROUP_REQUIRED for non-exempt)。
  getCurrentGroupId,
};

// 註冊 Firebase Auth token fetcher(對齊 ADR-002 + SSOT 第二份 §2)。
// 打 BE `POST /api/auth/firebase-token`(走既有 cookie + CSRF + refresh chain)。
// R41.6 起無 SERVICE_MODE gate(砍 mock layer);永遠註冊。
setFirebaseTokenFetcher(async () => {
  const envelope = await httpRequestWithRefresh<FirebaseTokenResponse>(
    httpConfig,
    'POST',
    '/api/auth/firebase-token',
  );
  if (!envelope.result) {
    throw new Error('[firebase-auth] BE response missing `result`');
  }
  return envelope.result;
});

/**
 * Services 集合。應用層只看型別:`services.authProvider.signIn(idToken)` 回傳
 * `Promise<LoginResult>`。
 *
 * R49 起 graph 走 `dagService`(per-doc OCC Beta v0.0.1),legacy `graphRepo`(LWW
 * 整份 PUT/PATCH)整支砍乾淨;canvas 端讀寫透過 `loadGraph` / `persistGraph` thin
 * wrapper 對 dagService 分流到 nodes / edges per-doc CRUD。
 *
 * R41.6 起全 http + 4 個 by-design client-side impl(`createRulesEngine` /
 * `createPrefsStore` / `createRealtimeBus`),砍 mock layer:
 * - `api` / `authProvider` / `uploadsService` / `brandKitsService` / `projectsService`
 *   / `jobsService` / `assetsService` / `assetLibrary` — 真 BE
 * - `dagService` — per-doc OCC(spec §3.6/3.7/4.2);Phase 3 canvas rewrite 後接 onSnapshot
 * - `rulesEngine` / `prefsStore` / `realtimeBus` — by-design
 *   client-side(implementation 收進對應 interface 檔)
 *
 *   - canvas write 後可透過 caller 端 emit `graph:updated`(server-push 由
 *     `useGraphSnapshotMerge` onSnapshot 接管)
 *   - 任何訂閱者(CoreCanvas / RunNotifications)拿同一條訊息流
 */
const bus = createRealtimeBus();
const uploadsService = createHttpUploadsService(httpConfig);
const brandKitsService = createHttpBrandKitsService(httpConfig);
// 提前建構 — assetLibrary.uploadAvatar(T2b)編排 /me/avatar intent+finalize 需要它。
const meService = createHttpMeService(httpConfig);

export const services = {
  authProvider: createHttpAuthProvider(httpConfig),
  rulesEngine: createRulesEngine(),
  uploadsService,
  brandKitsService,
  projectsService: createHttpProjectsService(httpConfig),
  jobsService: createHttpJobsService(httpConfig),
  assetsService: createHttpAssetsService(httpConfig),
  assetLibrary: createAssetLibrary({
    uploads: uploadsService,
    brandKits: brandKitsService,
    me: meService,
    gcsPut: createHttpGcsPut(),
  }),
  prefsStore: createPrefsStore(),
  realtimeBus: bus,
  // Phase Ads — Stage 1+2+3 共用 service(13 Stage 1 method;Stage 2/3 進來加 method group)
  adsService: createHttpAdsService(httpConfig),
  // R45 Beta v0.0.1 — Group N:M membership CRUD(spec §3.3)
  groupsService: createHttpGroupsService(httpConfig),
  // R45 Beta v0.0.1 — per-doc DAG OCC(spec §3.6/3.7/4.2;Phase 3 canvas 接)
  dagService: createHttpDagService(httpConfig),
  // R48.x — BE handoff 2026-05-25:atomic first login → GET /api/me 拿 current_group_id;
  // 切 group 走 PATCH /api/me + re-fetch /me 拿新 wallet。endpoints 不掛 L1.5。
  meService,
  // P3 — node catalog(docs/44):canvas mount 拉一次 cache 整個 session;TOOL 動態表單
  // params / model 下拉 / 估價都讀這份,FE 不 hardcode model id / 參數 / 價格。
  catalogService: createHttpCatalogService(httpConfig),
  // R146(docs/76 §8.1 / ADR-038 BE-7)— 內容檢查(合規)即時掃描;抽屜常駐檢查盒 debounce 呼叫。
  complianceService: createHttpComplianceService(httpConfig),
  // R106(docs/65)— 合約制工作區開通:platform-admin 建約(/admin/contracts)+
  // workspace-owner 自助管成員(/contracts/mine)。取代已移除的 billing(docs/64)。
  contractService: createHttpContractService(httpConfig),
  // T5(docs/50)— 說明知識庫(M-18,Phase 1 read-only;非 group-scoped)
  helpService: createHttpHelpService(httpConfig),
  // T1(docs/49 §8)— M-00 首頁儀表板聚合(group-scoped read)
  dashboardService: createHttpDashboardService(httpConfig),
  // T7(docs/53)— 素材庫(asset_libraries)讀取;T7 匯入精靈挑素材來源
  assetLibrariesService: createHttpAssetLibrariesService(httpConfig),
  // R105 — 金流(billing)整條移除(docs/64 合約制):billingService 已刪。錢包餘額仍走
  // meService(GET /api/me);消費扣點不變。
  // ADR-026 — canvas AI-assist(copy-assist / placement-expand)合一 service;
  // project-scoped POST + body idempotency_key。對接契約 SoT docs/44 handoff。
  canvasAiService: createHttpCanvasAiService(httpConfig),
  // R136(docs/76 §3 / ADR-035 雙色卡)— 綠卡「AI 協助填寫」。F3 才接 UI;BE 交付前
  // 無 caller(ideation 端點屆時退場,由本 service 取代)。
  assistService: createHttpAssistService(httpConfig),
  // R213(2026-08-07)— 素材內容檢查切 **真 BE**(R212 契約對帳 79 條收斂後):
  // dev 預設 VERTEX_MODE=stub(AI 判定項 ⚪、直算項/計費/進度全真,docs/87)。
  //
  // fixture **只在 demo flag 明確打開時**才進來(`localStorage['hai.checkDemo']='1'`
  // + reload)—— 它不是任何環境的預設實作:假報告帶著「會用 N 次額度」的承諾,
  // 誤當真的就是誤導計費。`pnpm dev` 與三個部署環境一律打真 BE。
  checkService: isCheckDemoEnabled()
    ? createFixtureCheckService()
    : createHttpCheckService(httpConfig),
  // R250 — 問題回報送出。⏳ BE 端點同步開發中:service 已接好,那顆按鈕由
  // `SUPPORT_REPORT_SUPPORTED` 一個常數 gate(BE 交付當天翻它即通,元件零改動)。
  supportService: createHttpSupportService(httpConfig),
  // R276 — 序號兌換點數(admin 產碼 / 使用者兌換)。⏳ BE 端點尚未交付
  // (docs/54 §settings.17):service 已接好,兩個入口由 `REDEEM_CODES_SUPPORTED`
  // 一個常數 gate(BE 交付、對完 wire 後翻它即通,元件零改動)。
  redeemCodesService: createHttpRedeemCodesService(httpConfig),
  // R392 — 推薦碼 × 業務歸屬 × 月結分潤(admin 面 A1–A23;BE MR !121)。入口顯示
  // 由「role=admin ∧ referral flag on」雙閘決定 —— flag 是 **runtime probe**
  // (useReferralAdminGate 打 A22,403 FEATURE_DISABLED / 404 無 slug = 藏入口),
  // 不設 `*_SUPPORTED` 常數:BE 部署到哪個環境,入口就自己在哪個環境長出來。
  referralService: createHttpReferralService(httpConfig),
  // R361 — 剪輯時間軸(edits)+「製作成品」出片(BE dev 已部,flag `edl_render` dev 開;
  // beta/prod fail-closed 由 BE flag 罩,FE 收 403 FEATURE_DISABLED 誠實顯示,不藏入口)。
  editsService: createHttpEditsService(httpConfig),
} as const;

export type Services = typeof services;
